import { CastingSlot } from "../models/castings.service"
import { MultiOpeningHours, WorkingDay } from "../models/offered-services.service"
import { planningIsUnavailabilityEvent } from "../models/plannings.service"
import { StoreProps } from "../stores/planning.store"
import { moment } from "./moment.service"
import { formatTime, parseTime, parseTimeRange } from "./utils.service"

export type Openable = { multi_opening_hours: MultiOpeningHours, working_days: WorkingDay[] }
export type UnserializedMultiOpeningHours = { [workingDay in WorkingDay]: [number, number][] }

/**
 * Computes the multi opnening hours as numerics (instead of formated string).
 * Days not worked are resolved as empty opening hours ([]).
 */
export function openableUnserialize (openable: Openable) {
  const unserialized: UnserializedMultiOpeningHours = {} as any

  for (let dayId = 1; dayId < 8; dayId++) {
    if (!openable.working_days.includes(dayId as WorkingDay)) unserialized[dayId as WorkingDay] = []
    else unserialized[dayId as WorkingDay] = openable.multi_opening_hours[dayId as WorkingDay].map(moh => [parseTime(moh[0]), parseTime(moh[1])])
  }

  return unserialized
}

export function openableSerialize (unserialized: UnserializedMultiOpeningHours & { midnightSafe?: never }) {
  const workingDays = ([1, 2, 3, 4, 5, 6, 7] as const).filter(dayId => unserialized[dayId].length)
  const multiOpeningHours: MultiOpeningHours = {}
  for (const dayId of workingDays) multiOpeningHours[dayId] = unserialized[dayId].map(moh => [formatTime(moh[0]), formatTime(moh[1])])
  return { working_days: workingDays, multi_opening_hours: multiOpeningHours }
}

/**
 * Computes the insection of opening hours of multiple workables.
 * For example, it may computes when both an openedService and an employee are 'open'.
 */
export function openableIntersection<T extends UnserializedMultiOpeningHours> (multiOpeningHoursArray: T[]) {
  const intersection: T = {} as any

  for (let dayId = 1; dayId < 8; dayId++) {
    intersection[dayId as WorkingDay] = multiOpeningHoursArray[0][dayId as WorkingDay].map(_ => [_[0], _[1]])
  
    for (let i = 1; i < multiOpeningHoursArray.length; i++) {
      const newDayIntersection: [number, number][] = []

      for (const firstTimeRange of intersection[dayId as WorkingDay]) {
        for (const secondTimeRange of multiOpeningHoursArray[i][dayId as WorkingDay]) {
          const interRange: [number, number] = [Math.max(firstTimeRange[0], secondTimeRange[0]), Math.min(firstTimeRange[1], secondTimeRange[1])]
          if (interRange[0] < interRange[1]) newDayIntersection.push(interRange)
        }
      }

      intersection[dayId as WorkingDay] = newDayIntersection
    }
  }

  return intersection
}

/**
 * Computes the union of opening hours of multiple workables.
 * It computes when at least one of the `multiOpeningHoursArray` is opened.
 */
export function openableUnion<T extends UnserializedMultiOpeningHours> (multiOpeningHoursArray: T[]) {
  const union: T = {} as any

  for (let dayId = 1; dayId < 8; dayId++) {
    const stack = multiOpeningHoursArray.slice(1).flatMap(multiOpeningHours => multiOpeningHours[dayId as WorkingDay])
    union[dayId as WorkingDay] = multiOpeningHoursArray[0][dayId as WorkingDay].map(_ => [_[0], _[1]])
    
    while (stack.length) {
      const timeRange = stack.pop()

      // if already included in `union`, just discard this `timeRange`.
      if (union[dayId as WorkingDay].find(moh => moh[0] <= timeRange[0] && timeRange[1] <= moh[1])) continue

      // check if `timeRange` lowed bound is included in one of `union`.
      const intersectBeforeIndex = union[dayId as WorkingDay].findIndex(moh => moh[0] <= timeRange[0] && timeRange[0] <= moh[1])
      if (intersectBeforeIndex !== -1) {
        const [intersectWith] = union[dayId as WorkingDay].splice(intersectBeforeIndex, 1)
        intersectWith[1] = timeRange[1]
        stack.push(intersectWith)
        continue
      }

      // check if `timeRange` upper bound is included in one of `union`.
      const intersectAfterIndex = union[dayId as WorkingDay].findIndex(moh => moh[0] <= timeRange[1] && timeRange[1] <= moh[1])
      if (intersectAfterIndex !== -1) {
        const [intersectWith] = union[dayId as WorkingDay].splice(intersectAfterIndex, 1)
        intersectWith[0] = timeRange[0]
        stack.push(intersectWith)
        continue
      }

      union[dayId as WorkingDay].push(timeRange)
    }

    union[dayId as WorkingDay].sort((a, b) => a[0] - b[0])
  }

  return union
}

/**
 * Implements the 'left - right' operator for multi opening hours.
 */
export function openableDifference<T extends UnserializedMultiOpeningHours> (left: T, right: T) {
  const difference: T = {} as any

  for (let dayId = 1; dayId < 8; dayId++) {
    difference[dayId as WorkingDay] = left[dayId as WorkingDay].map(_ => [_[0], _[1]])
    
    for (const timeRange of right[dayId as WorkingDay]) {
      const overlapsLowerIndex = difference[dayId as WorkingDay].findIndex(moh => moh[0] <= timeRange[0] && timeRange[0] <= moh[1])
      if (overlapsLowerIndex !== -1) {
        const overlapsLower = difference[dayId as WorkingDay][overlapsLowerIndex]
        if (overlapsLower[1] > timeRange[1]) difference[dayId as WorkingDay].splice(overlapsLowerIndex + 1, 0, [timeRange[1], overlapsLower[1]])

        overlapsLower[1] = timeRange[0]
        if (overlapsLower[1] <= overlapsLower[0]) difference[dayId as WorkingDay].splice(overlapsLowerIndex, 1)
      }

      const overlapsUpperIndex = difference[dayId as WorkingDay].findIndex(moh => moh[0] <= timeRange[1] && timeRange[1] <= moh[1])
      if (overlapsUpperIndex !== -1) {
        const overlapsUpper = difference[dayId as WorkingDay][overlapsUpperIndex]
        overlapsUpper[0] = timeRange[1]
        if (overlapsUpper[1] <= overlapsUpper[0]) difference[dayId as WorkingDay].splice(overlapsUpperIndex, 1)
      }
    }

    difference[dayId as WorkingDay].sort((a, b) => a[0] - b[0])
  }

  return difference
}

/**
 * Compute the complement set relative to a always available workable.
 * See https://en.wikipedia.org/wiki/Complement_(set_theory)
 * 
 * Returns { 1: [[0, 24]], ... 7: [[0, 24]] } - `multiOpeningHours`
 */
export function openableComplement (multiOpeningHours: UnserializedMultiOpeningHours & { midnightSafe?: never }) {
  return openableDifference({ 1: [[0, 24 ]], 2: [[0, 24 ]], 3: [[0, 24 ]], 4: [[0, 24 ]], 5: [[0, 24 ]], 6: [[0, 24 ]], 7: [[0, 24 ]] }, multiOpeningHours)
}

/**
 * Merges adjacent multi opening hours: { 1: [[12h, 14h], [14h, 17h]] } becomes { 1: [[12h, 17h]] }
 */
export function openableReduce<T extends UnserializedMultiOpeningHours> (multiOpeningHours: T) {
  const reduced: UnserializedMultiOpeningHours & { reduced: true } = {} as any
  for (let dayId = 1; dayId < 8; dayId++) reduced[dayId as WorkingDay] = openableReduceDay(multiOpeningHours[dayId as WorkingDay])
  return reduced
}

/**
 * Merge multi opening hours that overlaps: { 1: [[12h, 14h], [13h, 17h]] } becomes { 1: [[12h, 17h]] }
 */
export function openableReduceOverlapping<T extends UnserializedMultiOpeningHours> (multiOpeningHours: T) {
  const reduced: UnserializedMultiOpeningHours & { reduced: true } = {} as any

  for (let dayId = 1; dayId < 8; dayId++) {
    let previousLength: number

    do {
      // used this way, openableReduceDay is a convergent algorithm (a single pass may not merge all ranges).
      previousLength = (reduced[dayId as WorkingDay] || multiOpeningHours[dayId as WorkingDay]).length
      reduced[dayId as WorkingDay] = openableReduceDay(multiOpeningHours[dayId as WorkingDay])
    } while(previousLength !== reduced[dayId as WorkingDay].length)
  }

  return reduced
}

/**
 * Returns valid times to begin or to end a slot at some day in complex mode.
 * Duration parameters (minDurationSecs, durationStepSecs) are in seconds.
 * Time parameters (startedAtHour) and return values are in hours.
 * Internal loop and comparisons use seconds to avoid rounding issues.
 */
export function openableGetComplexTimes (multiOpeningHours: UnserializedMultiOpeningHours & { midnightSafe: true, reduced: true }, dayId: WorkingDay, minDurationSecs: number, options: { durationStepSecs?: number, isStart?: boolean, startedAtHour?: number }) {
  const isStart = options.isStart !== false
  const startedAtHour = options.startedAtHour || 0
  const durationStepSecs = options.durationStepSecs || 900
  const results = []

  const startedAtSecs = Math.round(startedAtHour * 3600)
  const maxSecs = Math.round((startedAtHour + 24) * 3600) - (isStart ? 0 : minDurationSecs)
  const openingHoursInSecs = multiOpeningHours[dayId].map(([from, to]) => [Math.round(from * 3600), Math.round(to * 3600)] as [number, number])

  for (let i = startedAtSecs; i < maxSecs; i += durationStepSecs) {
    // Valid start hour if continously available from i to i + minSlotDuration
    if (openingHoursInSecs.find(([from, to]) => from <= i && i <= to && from <= i + minDurationSecs && i + minDurationSecs <= to)) {
      results.push(isStart ? i / 3600 : (i + minDurationSecs) / 3600)
    }
  }

  return results
}

/**
 * Merges the adjacents opening range of each day with the first of following day.
 * Transforms { 1: [[20h, 24h]], 2: [[0h, 8h], [12h, 20h]] } into { 1: [[20h, 32h]], 2: [[0h, 8h], [12h, 20h]] }.
 * This way, checking if open on monday starting at 20h for a duration of 8 hours (over midnight) becomes easier.
 */
export function openableOverMidnight<T extends UnserializedMultiOpeningHours> (multiOpeningHours: T, numberOfNights = 1): T & { midnightSafe: true } {
  const overMidnightHours: T & { midnightSafe: true } = { ...multiOpeningHours } as any

  for (let currentMidnight = 1; currentMidnight <= numberOfNights; currentMidnight++) {
    for (let dayId = 1; dayId < 8; dayId++) {
      const currDay = overMidnightHours[dayId as WorkingDay]
      const nextDay = multiOpeningHours[nextDayId(dayId as WorkingDay, currentMidnight)]
      
      if (currDay.length && nextDay.length && currDay[currDay.length - 1][1] === 24 * currentMidnight && nextDay[0][0] === 0) {
        overMidnightHours[dayId as WorkingDay] = currDay.slice()
        overMidnightHours[dayId as WorkingDay][currDay.length - 1] = [currDay[currDay.length - 1][0], nextDay[0][1] + 24 * currentMidnight]
      }
    }
  }

  return overMidnightHours
}

/**
 * Revert openableOverMidnight().
 * Transforms { 1: [[20h, 32h]], 2: [[0h, 8h], [12h, 20h]] } into { 1: [[20h, 24h]], 2: [[0h, 8h], [12h, 20h]] }.
 */
export function openableMidnightTo24h (multiOpeningHours: UnserializedMultiOpeningHours & { midnightSafe?: true }) {
  // subset of multiOpeningHours that is already between 0 and 24h ([20h, 32h] => [20h, 24h])
  const beforeMidnight: UnserializedMultiOpeningHours = {} as any

  // subset of multiOpeningHours that is after midnight, on which modulo 24h is applyed
  const afterMidnight: UnserializedMultiOpeningHours = {} as any

  for (const dayId of [1, 2, 3, 4, 5, 6, 7] as const) {
    beforeMidnight[dayId] = multiOpeningHours[dayId].filter(moh => moh[0] < 24).map(moh => [moh[0], Math.min(24, moh[1])])
    afterMidnight[nextDayId(dayId)] = multiOpeningHours[dayId].filter(moh => moh[1] > 24).map(moh => [Math.max(24, moh[0]) % 24, moh[1] % 24])
  }

  return openableUnion([beforeMidnight, afterMidnight])
}

export function openableStartHours<T extends UnserializedMultiOpeningHours & { startHours?: never }> (multiOpeningHours: T, duration: number) {
  const startHours: T & { startHours: true } = {} as any
  for (let dayId = 1; dayId < 8; dayId++) startHours[dayId as WorkingDay] = openableStartHoursForDayId(multiOpeningHours[dayId as WorkingDay], duration)
  return startHours
}

export function openableStartHoursForDayId (mohArray: UnserializedMultiOpeningHours[1], duration: number): UnserializedMultiOpeningHours[1] {
  return mohArray
    .map(moh => [moh[0], Math.min(moh[1] - duration, 24)] as [number, number])
    .filter(moh => moh[0] <= moh[1])
}

export function openableStartHoursAtDate (date: Date, multiOpeningHours: UnserializedMultiOpeningHours & { startHours: true }, waitingOrderHoursPeriod: number, step: number, now = new Date()) {
  const waitingPeriod = openableRoundTimeUpper(moment(now).add(waitingOrderHoursPeriod, 'h').toDate(), step)
  const startHours: [number, number][] = []
  const baseDate = moment(date).startOf('day')

  for (const moh of multiOpeningHours[openableDateToWorkingDay(date)]) {
    const startOfRange = moment.max(waitingPeriod, baseDate.clone().hours(moh[0]).minutes(moh[0] * 60 % 60))

    if (baseDate.clone().hours(moh[1]).minutes(moh[1] * 60 % 60).isSameOrAfter(startOfRange)) {
      startHours.push([startOfRange.hours() + startOfRange.minutes() / 60 + (moh[0] > 24 ? 24 : 0), moh[1]])
    }
  }

  return startHours
}

/**
 * Normaly, multiOpeningHours are specified for each day.
 * This method returns multiOpeningHours for a floating week (begining at `fromDayId`) into a single array.
 * See openables.service.spec.ts for examples.
 */
export function openableFlatWeekOpeningHours<T extends UnserializedMultiOpeningHours & { midnightSafe?: never }> (multiOpeningHours: T, fromDayId: WorkingDay) {
  const floatingWeekOpeningHours = []

  for (let i = 0; i < 7; i++) {
    floatingWeekOpeningHours.push(...multiOpeningHours[nextDayId(fromDayId, i)].map(([from, to]) => [from + i * 24, to + i * 24]))
  }

  return floatingWeekOpeningHours
}

/**
 * Returns asap offers for which there is an enough long opening hours available.
 */
export function openableGetAvailableAsapOffers (multiOpeningHours: UnserializedMultiOpeningHours & { midnightSafe?: never }, asapOffers: number[], duration: number, step: number, now = new Date()) {
  const roundedUpperNow = openableRoundTimeUpper(now, step)
  const roundedUpperTime = roundedUpperNow.hours() + roundedUpperNow.minutes() / 60
  
  // start hours from now (considering both date and time) for given `duration` over the next 7 days
  const startHoursOverWeekFromNow = openableFlatWeekOpeningHours(multiOpeningHours, openableDateToWorkingDay(now))
    .map(([from, to]) => [Math.max(from, roundedUpperTime), to - duration])
    .filter(([from, to]) => to >= from)

  return asapOffers.filter(asapOffer => startHoursOverWeekFromNow.find(moh => moh[0] <= asapOffer + roundedUpperTime))
}

export function openableFromCastingSlotAllowedPatterns (allowedPatterns: CastingSlot['allowed_patterns']) {
  const unserialized: UnserializedMultiOpeningHours = {} as any
  for (let dayId = 1; dayId < 8; dayId++) unserialized[dayId as WorkingDay] = []

  for (const allowedPattern of allowedPatterns) {
    if (allowedPattern.additional_days) {
      for (let i = 0; i <= allowedPattern.additional_days; i++) {
        unserialized[nextDayId(allowedPattern.day_id, i)] = [[0, 24]]
      }
    } else {
      // warning: allowedPattern ranges may overlaps!
      unserialized[nextDayId(allowedPattern.day_id, 0)].push(parseTimeRange(allowedPattern.from_hour, allowedPattern.until_hour))
    }
  }

  return openableReduceOverlapping(unserialized)
}

export function openableRoundTimeUpper (date: Date, step: number) {
  // +15 minutes, rounded to the upper step (this extra duration covers the time to submit the casting and reach the server side validation)
  const roundedUpperNow = moment(date).add(15, 'minutes').seconds(0).milliseconds(0)
  roundedUpperNow.minutes(Math.ceil(roundedUpperNow.minutes() / step / 60) * step * 60)
  return roundedUpperNow
}

export function openabledDatifiedMultiOpeningHoursBetween<T extends UnserializedMultiOpeningHours & { midnightSafe?: never }> (
  multiOpeningHours: T,
  startDate: moment.Moment,
  endDate: moment.Moment
): [moment.Moment, moment.Moment][] {
  const ranges: [moment.Moment, moment.Moment][] = []
  
  for (const current = startDate.clone().startOf('day'); current.isSameOrBefore(endDate); current.add(1, 'day')) {
    const dayOpenings = multiOpeningHours[current.isoWeekday() as WorkingDay]

    for (const openingInterval of (dayOpenings || [])) {
      const [fromHour, toHour] = openingInterval
      const from = current.clone().hour(Math.floor(fromHour)).minute(Math.round((fromHour % 1) * 60)).second(0).millisecond(0)
      const to = current.clone().hour(Math.floor(toHour)).minute(Math.round((toHour % 1) * 60)).second(0).millisecond(0)
      ranges.push([from, to])
    }
  }

  // merge together the continuous ranges
  ranges.sort((a, b) => a[0].valueOf() - b[0].valueOf())

  for (let i = 0; i < ranges.length - 1;) {
    if (ranges[i][1].isSame(ranges[i + 1][0])) {
      ranges[i][1] = ranges[i + 1][1]
      ranges.splice(i + 1, 1)
    } else {
      i++
    }
  }

  for (let i = 0; i < ranges.length; i++) {
    if (ranges[i][0].isBefore(startDate)) ranges[i][0] = startDate.clone()
    if (ranges[i][1].isAfter(endDate)) ranges[i][1] = endDate.clone()
  }

  return ranges.filter(range => range[1].isAfter(range[0]))
}

export function openableDateToWorkingDay (date: Date) {
  return (date.getDay() === 0 ? 7 : date.getDay()) as WorkingDay
}

export function nextDayId (dayId: number, count = 1) {
  return ((dayId + count) % 7 ? (dayId + count) % 7 : 7) as WorkingDay
}

export function openableIsUnavailableFullDay (
  employee: Openable,
  date: moment.Moment,
  events: StoreProps['data']['events'],
  oneshotAvailabilities: (Omit<StoreProps['data']['oneshotAvailabilities'][number], 'start_date' | 'end_date'> & Record<'start_date' | 'end_date', string | moment.Moment>)[]
) {
  const dayId = openableDateToWorkingDay(date.toDate())
  const dailyHours = openableUnserialize(employee)[dayId]
  if (!dailyHours.length && !oneshotAvailabilities.length) return true

  const startOfDay = date.clone().startOf('day')
  const endOfDay = date.clone().endOf('day')

  const dateTimes = dailyHours
    .flat()
    .map(hourWithMinutes => date.clone().minutes(Math.round(hourWithMinutes * 60)))
    .concat(oneshotAvailabilities.flatMap(availability => [availability.start_date, availability.end_date].map(_ => moment(_))))
    .map(dateTime => moment.min(moment.max(dateTime, startOfDay), endOfDay) /* coerceIn dateTime between startOfDay and endOfDay */)

  const openingHour = moment.min(dateTimes), closingHour = moment.max(dateTimes)
  if (events.some(ev => planningIsUnavailabilityEvent(ev) && openingHour >= ev.start_date && closingHour <= ev.end_date)) return true
  return false
}

function openableReduceDay (dailyMultiOpeningHours: [number, number][]) {
  const reduced: [number, number][] = dailyMultiOpeningHours.map(_ => [_[0], _[1]])
  reduced.sort((a, b) => a[0] - b[0])

  for (let i = 0; i < reduced.length - 1; i++) {
    if (reduced[i][1] >= reduced[i + 1][0]) { // merge [12h, 14h] + [14h, 17h] into [12h, 17h]
      reduced[i][1] = Math.max(reduced[i][1], reduced[i + 1][1])
      reduced.splice(i + 1, 1)
      i--
    }
  }

  return reduced
}
